// Narrative data utilities for aggregating facility data and calculating metrics
// Used to power data-first narrative visualizations

import {
  calculateServiceCoverage,
  calculateInfrastructureReadiness,
  calculateFacilityLevelDistribution,
  staffCategoriesList,
} from './chartUtils'
import { aggregateEducationLocationData } from './educationNarrativeDataUtils'
import {
  groupFacilitiesByLocation,
  calculateLocationStats,
  generateLocationRankings,
  createServiceCoverageHeatmap,
  getComparisonTargets,
} from './comparisonUtils'

/**
 * Find location by ID in the locations hierarchy
 */
export function findLocationById(locations, locationId) {
  if (!locations || !locationId) return null

  // Check districts
  for (const district of locations.districts || []) {
    if (district.id === locationId) {
      return {
        ...district,
        type: 'district',
        parent: null,
        children: district.subcounties || null,
      }
    }

    // Check subcounties
    for (const subcounty of district.subcounties || []) {
      if (subcounty.id === locationId) {
        return {
          ...subcounty,
          type: 'subcounty',
          parent: district,
          children: subcounty.parishes || null,
        }
      }

      // Check parishes
      for (const parish of subcounty.parishes || []) {
        if (parish.id === locationId) {
          return {
            ...parish,
            type: 'parish',
            parent: subcounty,
            children: parish.villages || null,
          }
        }

        // Check villages
        for (const village of parish.villages || []) {
          if (village.id === locationId) {
            return {
              ...village,
              type: 'village',
              parent: parish,
              children: null,
            }
          }
        }
      }
    }
  }

  return null
}

/**
 * Filter facilities by location code prefix
 */
export function filterFacilitiesByLocation(facilities, location) {
  if (!facilities || !location?.code) return []

  return facilities.filter(
    facility =>
      facility.location_code && facility.location_code.startsWith(location.code)
  )
}

/**
 * Calculate staffing distribution from facilities
 */
export function calculateStaffingDistribution(facilities) {
  if (!facilities || facilities.length === 0) {
    return {
      totals: {},
      averages: {},
      distribution: [],
    }
  }

  const totals = {}
  staffCategoriesList.forEach(staff => {
    totals[staff.id] = 0
  })

  // Sum up all staff counts
  facilities.forEach(facility => {
    staffCategoriesList.forEach(staff => {
      const count = facility[staff.id] || 0
      totals[staff.id] += typeof count === 'number' ? count : 0
    })
  })

  // Calculate averages
  const averages = {}
  staffCategoriesList.forEach(staff => {
    averages[staff.id] =
      facilities.length > 0 ? totals[staff.id] / facilities.length : 0
  })

  // Create distribution array for charts
  const distribution = staffCategoriesList.map(staff => ({
    name: staff.name,
    value: totals[staff.id],
    average: averages[staff.id],
    icon: staff.icon,
    category: staff.category,
  }))

  return {
    totals,
    averages,
    distribution: distribution.filter(d => d.value > 0), // Only include non-zero staff
  }
}

/**
 * Identify critical infrastructure gaps
 */
export function identifyInfrastructureGaps(infrastructureReadiness) {
  if (!infrastructureReadiness || infrastructureReadiness.length === 0)
    return []

  return infrastructureReadiness
    .filter(item => item.percentage < 70) // Consider <70% as a gap
    .map(item => ({
      name: item.name,
      percentage: item.percentage,
      severity:
        item.percentage < 30 ? 'high' : item.percentage < 50 ? 'medium' : 'low',
    }))
    .sort((a, b) => a.percentage - b.percentage) // Worst gaps first
}

/**
 * Get sub-location comparisons for the current location
 */
export function getSubLocationComparisons(
  facilities,
  location,
  locations,
  maxDepth = 'village'
) {
  if (!location || !locations) return null

  // Determine what sub-locations to compare based on current location type
  // Helper function to get district ID from location
  const getDistrictId = loc => {
    if (loc.type === 'district') return loc.id
    if (loc.type === 'subcounty') return loc.parent?.id
    if (loc.type === 'parish') return loc.parent?.parent?.id
    if (loc.type === 'village') return loc.parent?.parent?.parent?.id
    return null
  }

  // Helper function to get subcounty ID from location
  const getSubcountyId = loc => {
    if (loc.type === 'subcounty') return loc.id
    if (loc.type === 'parish') return loc.parent?.id
    if (loc.type === 'village') return loc.parent?.parent?.id
    return null
  }

  // Helper function to get parish ID from location
  const getParishId = loc => {
    if (loc.type === 'parish') return loc.id
    if (loc.type === 'village') return loc.parent?.id
    return null
  }

  const comparisonTargets = getComparisonTargets(
    locations,
    getDistrictId(location),
    getSubcountyId(location),
    getParishId(location),
    location.type === 'village' ? location.id : null,
    maxDepth
  )

  if (!comparisonTargets.targets || comparisonTargets.targets.length === 0) {
    return null
  }

  // Group facilities by sub-locations
  const grouped = groupFacilitiesByLocation(
    facilities,
    comparisonTargets.targets,
    comparisonTargets.level
  )

  // Calculate stats for each location
  const locationStats = grouped.map(group => calculateLocationStats(group))

  // Generate rankings
  const rankings = generateLocationRankings(
    locationStats,
    'compositeScore',
    'desc'
  )

  // Create heatmap data
  const heatmap = createServiceCoverageHeatmap(locationStats)

  return {
    type: comparisonTargets.type,
    level: comparisonTargets.level,
    context: comparisonTargets.context,
    rankings,
    heatmap,
  }
}

/**
 * Aggregate all location data for narrative dashboard - HEALTH SPECIFIC
 * This function aggregates health facility data only
 */
export function aggregateHealthLocationData(
  facilities,
  locationId,
  locations,
  maxDepth = 'village'
) {
  // Find location
  const location = findLocationById(locations, locationId)
  if (!location) {
    throw new Error(`Location ${locationId} not found`)
  }

  // Filter facilities for this location
  const locationFacilities = filterFacilitiesByLocation(facilities, location)

  if (locationFacilities.length === 0) {
    // Return empty data structure if no facilities
    return {
      location,
      metrics: {
        totalFacilities: 0,
        facilityDistribution: [],
        serviceCoverage: [],
        infrastructureReadiness: [],
        infrastructureGaps: [],
        staffingDistribution: {
          totals: {},
          averages: {},
          distribution: [],
        },
      },
      subLocationComparisons: null,
    }
  }

  // Calculate metrics
  const facilityDistribution =
    calculateFacilityLevelDistribution(locationFacilities)
  const serviceCoverage = calculateServiceCoverage(locationFacilities)
  const infrastructureReadiness =
    calculateInfrastructureReadiness(locationFacilities)
  const infrastructureGaps = identifyInfrastructureGaps(infrastructureReadiness)
  const staffingDistribution = calculateStaffingDistribution(locationFacilities)

  // Get sub-location comparisons (if applicable)
  const subLocationComparisons = getSubLocationComparisons(
    facilities, // Use ALL facilities, not just location's facilities
    location,
    locations,
    maxDepth
  )

  return {
    location,
    metrics: {
      totalFacilities: locationFacilities.length,
      facilityDistribution,
      serviceCoverage,
      infrastructureReadiness,
      infrastructureGaps,
      staffingDistribution,
    },
    subLocationComparisons,
  }
}

/**
 * Category router for aggregating location data
 * Routes to appropriate aggregation function based on category
 */
export function aggregateLocationData(
  facilities,
  locationId,
  locations,
  maxDepth = 'village',
  category = 'health'
) {
  if (category === 'education') {
    return aggregateEducationLocationData(
      facilities,
      locationId,
      locations,
      maxDepth
    )
  }

  // Default to health
  return aggregateHealthLocationData(
    facilities,
    locationId,
    locations,
    maxDepth
  )
}

/**
 * Calculate key statistics for overview section
 */
export function calculateKeyStatistics(metrics) {
  if (!metrics || metrics.totalFacilities === 0) {
    return {
      totalFacilities: 0,
      avgServiceCoverage: 0,
      avgInfrastructure: 0,
      criticalGaps: 0,
      totalStaff: 0,
    }
  }

  const avgServiceCoverage =
    metrics.serviceCoverage.length > 0
      ? Math.round(
          metrics.serviceCoverage.reduce((sum, s) => sum + s.percentage, 0) /
            metrics.serviceCoverage.length
        )
      : 0

  const avgInfrastructure =
    metrics.infrastructureReadiness.length > 0
      ? Math.round(
          metrics.infrastructureReadiness.reduce(
            (sum, i) => sum + i.percentage,
            0
          ) / metrics.infrastructureReadiness.length
        )
      : 0

  const criticalGaps = metrics.infrastructureGaps.filter(
    gap => gap.severity === 'high'
  ).length

  const totalStaff = metrics.staffingDistribution.distribution.reduce(
    (sum, staff) => sum + staff.value,
    0
  )

  return {
    totalFacilities: metrics.totalFacilities,
    avgServiceCoverage,
    avgInfrastructure,
    criticalGaps,
    totalStaff,
  }
}
